import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Field, Form, Formik, FormikErrors } from 'formik';

import { IdentityTypeDirectors, InputField } from 'src/features/common';
import { IdItem, OnboardingDocumentTypeEnum } from 'src/features/onboarding/types/upload-documents';
import { idMask, stripWhitespace } from 'src/helpers/formatting';
import useImageUpload from 'src/features/onboarding/hooks/use-image-upload';
import { ImageUploadActionType } from 'src/features/onboarding/types/image-upload-reducer-actions';
import { Environment } from 'src/Environment';
import { CloseIconDark } from 'src/assets/images';
import { ImageUploadJourney } from 'src/features/onboarding/context/image-upload-context';
import { privacyNoticeUrl } from 'src/common';
import { NotifyPosition, notify, Toast } from 'src/features/common/components/toast/toast';
import 'react-toastify/dist/ReactToastify.css';

type SelectIdTypeFormValues = {
  idType?: { value: OnboardingDocumentTypeEnum; label: string } | string;
  selectedId: IdItem | string;
};

type SelectIdTypeFormErrors = {
  idType: string;
  selectedId: string;
};

type IdTypeListItem = {
  value: OnboardingDocumentTypeEnum;
  label: string;
};

function SelectIdTypeModal() {
  const { imageUploadState, imageUploadDispatch } = useImageUpload();
  const [idList, setIdList] = useState<IdItem[]>([]);
  const [idTypes, setIdTypes] = useState<IdTypeListItem[]>([
    {
      value: OnboardingDocumentTypeEnum.ID_CARD,
      label: 'Smart ID Card',
    },
    {
      value: OnboardingDocumentTypeEnum.ID_BOOK,
      label: 'Green Barcoded ID Book',
    },
    {
      value: OnboardingDocumentTypeEnum.PASSPORT,
      label: 'Passport',
    },
    {
      value: OnboardingDocumentTypeEnum.REFUGEE_LETTER,
      label: 'Refugee',
    },
    {
      value: OnboardingDocumentTypeEnum.ASYLUM_SEEKER,
      label: 'Asylum Seeker',
    },
  ]);
  const [nextEnabled, setNextEnabled] = useState<boolean>(false);

  const dialogRef = useRef<HTMLDialogElement>(null);

  const imageJoinEnabled = useMemo(
    (): boolean => !Environment.current.disabledFeatures.imageJoin,
    [],
  );

  const hasOneId = useMemo(
    (): boolean =>
      imageUploadState.ids.length === 1 ||
      imageUploadState.refugeeIds.length === 1 ||
      imageUploadState.asylumIds.length === 1,
    [],
  );

  const isIdOrPassport = useMemo(
    (): boolean =>
      !!imageUploadState.ids &&
      imageUploadState.ids.some(
        (id) =>
          id.idType === IdentityTypeDirectors.RSA_ID ||
          id.idType === IdentityTypeDirectors.PASSPORT,
      ),
    [],
  );

  const isSolePropPassport = useMemo(
    (): boolean =>
      !!imageUploadState.ids &&
      imageUploadState.ids.length === 1 &&
      imageUploadState.ids.every((id) => id.idType !== IdentityTypeDirectors.RSA_ID),
    [],
  );

  const isSolePropId = useMemo(
    (): boolean =>
      !!imageUploadState.ids &&
      imageUploadState.ids.length === 1 &&
      imageUploadState.ids.every((id) => id.idType === IdentityTypeDirectors.RSA_ID),
    [],
  );

  const isIDDocType = useMemo(
    (): boolean => imageUploadState.documentType === OnboardingDocumentTypeEnum.ID_TYPE,
    [],
  );

  const submitButtonText = {
    [ImageUploadJourney.CAMERA]: 'Open camera',
    [ImageUploadJourney.UPLOAD]: 'Upload',
  };

  const getIdType = (docType: string) => {
    switch (docType) {
      case OnboardingDocumentTypeEnum.ID_CARD:
      case OnboardingDocumentTypeEnum.ID_BOOK:
      case 'ID':
        return 'an ID';

      case OnboardingDocumentTypeEnum.PASSPORT:
      case OnboardingDocumentTypeEnum.WORK_PERMIT:
      case OnboardingDocumentTypeEnum.PERMANENT_RESIDENCY:
        return 'a Passport';

      case OnboardingDocumentTypeEnum.ASYLUM_SEEKER:
        return 'an Asylum Seeker';

      case OnboardingDocumentTypeEnum.REFUGEE_LETTER:
        return 'a Refugee';

      default:
        return 'an Identity';
    }
  };

  const hasOneSmartCard = (idNumber: SelectIdTypeFormValues['selectedId']): boolean => {
    const id = typeof idNumber !== 'string' ? idNumber.idNumber : idNumber;

    const docItem = imageUploadState.idDocsUploaded.find(
      (docsUploaded) => docsUploaded.idNumber === stripWhitespace(id),
    );
    return !!docItem && docItem.numOfSmartCards === 1;
  };

  const isSmartCard = (idType: SelectIdTypeFormValues['idType']): boolean => {
    return (
      !!idType && typeof idType !== 'string' && idType.value === OnboardingDocumentTypeEnum.ID_CARD
    );
  };

  const isGreenBook = (idType: SelectIdTypeFormValues['idType']): boolean => {
    return (
      !!idType && typeof idType !== 'string' && idType.value === OnboardingDocumentTypeEnum.ID_BOOK
    );
  };

  const isIdentityDoc = (idType: SelectIdTypeFormValues['idType']): boolean => {
    return (
      !!idType &&
      typeof idType !== 'string' &&
      (idType.value === OnboardingDocumentTypeEnum.ID_BOOK ||
        idType.value === OnboardingDocumentTypeEnum.ID_CARD ||
        idType.value === OnboardingDocumentTypeEnum.ID_TYPE)
    );
  };

  const requestBothSides = (values: SelectIdTypeFormValues): boolean => {
    switch (true) {
      case hasOneSmartCard(values.selectedId):
        return false;
      case isSmartCard(values.idType):
        return imageJoinEnabled ? true : false;
      case isGreenBook(values.idType):
        return false;
      case !isIdentityDoc(values.idType):
        return false;

      default:
        return imageJoinEnabled;
    }
  };

  const getIdTypeValue = (idType: SelectIdTypeFormValues['idType']): OnboardingDocumentTypeEnum => {
    if (idType === undefined) return OnboardingDocumentTypeEnum.ID_TYPE;
    return typeof idType !== 'string' ? idType.value : (idType as OnboardingDocumentTypeEnum);
  };

  const initialIdType = (): string | SelectIdTypeFormValues['idType'] | undefined => {
    switch (imageUploadState.documentType) {
      case OnboardingDocumentTypeEnum.ID_TYPE:
        return isSolePropPassport ? 'Passport' : undefined;

      default:
        return '';
    }
  };

  const initialSelectedId = (): string => {
    switch (imageUploadState.documentType) {
      case OnboardingDocumentTypeEnum.ID_TYPE:
      case OnboardingDocumentTypeEnum.PASSPORT:
        return imageUploadState.ids[0].idNumber;
      default:
        return '';
    }
  };

  const idTypeMatch = (ids: IdItem[], idNumber: string, selectedIdType?: string): boolean => {
    const stateSelectedId = ids.find((id) => stripWhitespace(id.idNumber) === idNumber);
    if (!stateSelectedId) return false;
    if (stateSelectedId && stateSelectedId.idType === IdentityTypeDirectors.RSA_ID) {
      return (
        selectedIdType === OnboardingDocumentTypeEnum.ID_BOOK ||
        selectedIdType === OnboardingDocumentTypeEnum.ID_CARD
      );
    }

    if (stateSelectedId && stateSelectedId.idType === IdentityTypeDirectors.PASSPORT) {
      return selectedIdType === OnboardingDocumentTypeEnum.PASSPORT;
    }

    return false;
  };

  const initialValues: SelectIdTypeFormValues = {
    idType: initialIdType(),
    selectedId: hasOneId ? initialSelectedId() : '',
  };

  const validate = (values: SelectIdTypeFormValues) => {
    const errors: FormikErrors<SelectIdTypeFormErrors> = {};

    const invalidId = !values.selectedId || values.selectedId === '';
    if (idList.length > 0 && invalidId && !!values?.idType) {
      errors.selectedId = `Please select ${getIdType(
        typeof values?.idType === 'string' ? values?.idType : values?.idType?.value,
      )} number from the list`;
    }

    if (typeof values.idType !== 'string' && (!values.idType || !values.idType.value)) {
      errors.idType = 'Please select an id type from the list';
    }

    setNextEnabled(Object.keys(errors).length === 0);

    return errors;
  };

  const submit = (values: SelectIdTypeFormValues) => {
    const selectedIdType = typeof values.idType === 'string' ? values.idType : values.idType?.value;

    const idNumber = stripWhitespace(
      typeof values.selectedId === 'string' ? values.selectedId : values.selectedId.idNumber,
    );

    // Check if idType matches the idType selected
    const validIdType = idTypeMatch(imageUploadState.ids, idNumber, selectedIdType);
    if (!validIdType) {
      notify({
        children: `Incorrect ID type selected`,
        autoClose: 5000,
        position: NotifyPosition.top_center,
        type: 'info',
      });
      return;
    }

    const uploadedDoc = imageUploadState.documents.find((doc) => doc.idNumber === idNumber);
    if (
      uploadedDoc &&
      uploadedDoc.documentType === OnboardingDocumentTypeEnum.ID_CARD &&
      selectedIdType === OnboardingDocumentTypeEnum.ID_BOOK
    ) {
      notify({
        children: `Cannot upload Green Barcoded ID Book. Please select Smart ID Card`,
        autoClose: 5000,
        position: NotifyPosition.top_center,
        type: 'info',
      });
      return;
    }

    imageUploadDispatch({
      type: ImageUploadActionType.END_SELECT_ID_TYPE,
      showModal: true,
      requestBothSides: requestBothSides(values),
      idNumber,
      idDocType: getIdTypeValue(values.idType),
      openCamera: imageUploadState.journey === ImageUploadJourney.CAMERA,
    });
  };

  const onClose = () => {
    imageUploadDispatch({
      type: ImageUploadActionType.CLOSE_SELECT_ID_TYPE,
      showModal: false,
      idNumber: '',
    });
    if (dialogRef && dialogRef.current) {
      dialogRef.current.close();
    }
  };

  const filterIdTypes = (selectedId: SelectIdTypeFormValues['selectedId']) => {
    const selectedIdValue = typeof selectedId === 'string' ? selectedId : selectedId.idNumber;
    const idNumber = stripWhitespace(selectedIdValue);
    const idDocUploaded = imageUploadState.idDocsUploaded.find((doc) => doc.idNumber === idNumber);

    if (idDocUploaded) {
      const smartCardUploadedOnly =
        idDocUploaded.numOfSmartCards === 1 && idDocUploaded.numOfGreenBooks === 0;
      return smartCardUploadedOnly
        ? idTypes.filter((idType) => idType.value === OnboardingDocumentTypeEnum.ID_CARD)
        : idTypes;
    }
    return idTypes;
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    event.preventDefault();
    if (event.key == 'Escape') {
      imageUploadDispatch({
        type: ImageUploadActionType.CLOSE_SELECT_ID_TYPE,
        showModal: false,
        idNumber: '',
      });
      dialogRef.current?.close();
    }
    return;
  };

  const hasRequiredIdTypes = (idNumber: string) => {
    const requiredSmartCards = imageJoinEnabled ? 2 : 1;
    const idDoc = imageUploadState.idDocsUploaded.find(
      (idDoc) => idDoc.idNumber === stripWhitespace(idNumber),
    );

    return (
      idDoc &&
      (idDoc.numOfGreenBooks === 1 ||
        idDoc.numOfSmartCards === requiredSmartCards ||
        idDoc.numOfPassports === 1)
    );
  };

  useEffect(() => {
    dialogRef.current?.addEventListener('keydown', handleKeyDown);

    return () => {
      dialogRef.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (isIdOrPassport && imageUploadState.documentType == OnboardingDocumentTypeEnum.ID_TYPE) {
      setIdTypes((prev) =>
        prev.filter(
          (type) =>
            type.value === OnboardingDocumentTypeEnum.ID_CARD ||
            type.value === OnboardingDocumentTypeEnum.ID_BOOK ||
            type.value === OnboardingDocumentTypeEnum.PASSPORT,
        ),
      );
    }
  }, []);

  useEffect(() => {
    if (
      imageUploadState.showModal &&
      imageUploadState.stepName === ImageUploadActionType.SELECT_ID_TYPE &&
      dialogRef
    ) {
      dialogRef.current?.showModal();
    }
  }, [imageUploadState.showModal]);

  useEffect(() => {
    setIdList(imageUploadState.ids.filter((id) => !hasRequiredIdTypes(id.idNumber)));
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className="flex flex-col items-center w-full gap-4 p-10 bg-white max-w-130 min-h-188 rounded-20 backdrop:opacity-95 backdrop:bg-black"
    >
      <Toast />
      <button className="absolute w-8 h-8 right-10" onClick={onClose}>
        <img src={CloseIconDark} alt="close" />
      </button>

      <header className="flex flex-col items-center gap-6 mt-10 md:mt-20">
        <h1 className="text-4xl text-grey-750">Select an ID Type</h1>
        <p className="text-base text-grey-750 max-w-[505px] text-center">
          Ensure you are in a <span className="font-bold">well lit area </span>and that there is{' '}
          <span className="font-bold">no glare or reflection</span> in the picture.
        </p>
      </header>

      <Formik<SelectIdTypeFormValues>
        initialValues={initialValues}
        validate={validate}
        onSubmit={submit}
        validateOnMount
      >
        {({ values }) => (
          <Form className="flex flex-col gap-4 w-full max-w-[505px]">
            {(hasOneId && !isSolePropId) || !isIDDocType ? (
              <Field
                name="idType"
                type="tel"
                placeholder="Selected an ID type"
                fieldType="standard"
                component={InputField}
                className="top-field bottom-field"
                disabled
              />
            ) : (
              <Field
                name="idType"
                type="select"
                placeholder="Select ID type"
                items={filterIdTypes(values.selectedId)}
                getItemDisplayValue={(item) => item.label}
                component={InputField}
                fieldType="select"
                className="top-field bottom-field"
              />
            )}

            {hasOneId ? (
              <Field
                name="selectedId"
                type="tel"
                placeholder={`Selected ${getIdType(
                  values?.idType && typeof values.idType !== 'string' ? values.idType.value : '',
                )} number`}
                fieldType="standard"
                mask={idMask}
                component={InputField}
                className="top-field bottom-field"
                disabled
              />
            ) : (
              <Field
                name="selectedId"
                type="select"
                placeholder={`Select ${getIdType(
                  values?.idType && typeof values.idType !== 'string' ? values.idType.value : '',
                )} number`}
                items={idList}
                getItemDisplayValue={(item: IdItem) => item.idNumber}
                component={InputField}
                fieldType="select"
                className="top-field bottom-field"
              />
            )}
            <div className="flex flex-col w-full max-w-[505px] mt-4">
              <p className="text-sm text-center text-grey-750">
                We use industry-leading encryption and security methods when handling your documents
              </p>
              <p className="text-sm text-center text-grey-750">
                Your details are not shared with anyone. To find out more, read our{' '}
                <a
                  href={privacyNoticeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-grey-750"
                >
                  privacy statement
                </a>
                .
              </p>
            </div>

            <div className="flex gap-2 md:justify-between">
              <button
                className="h-10 text-base font-medium text-center text-black bg-white border-2 border-black border-solid rounded-8 w-44"
                type="button"
                onClick={onClose}
              >
                Back
              </button>

              <button
                className={`${
                  nextEnabled ? 'bg-red-600 text-white' : 'bg-grey-700 text-grey-300 opacity-100'
                } rounded-8  w-44 h-10 text-center  text-base font-medium`}
                type="submit"
                disabled={!nextEnabled}
              >
                {submitButtonText[imageUploadState.journey ?? ImageUploadJourney.UPLOAD]}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </dialog>
  );
}

export default SelectIdTypeModal;
